import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { IOutputAdjustment } from "../interfaces/OutputAdjustment";
import { useOnClickOutside } from "../../../../../../../../hooks/useOnClickOutside";
import { FaTimes } from "react-icons/fa";

interface IModelWindowProps {
  onClose: () => void;
  outputAdj: IOutputAdjustment;
  onOutputAdjChange: (outputs: IOutputAdjustment) => void;
  isSubmit: boolean;
}

const ModalOutputAdjustments: React.FC<IModelWindowProps> = ({
  onClose,
  outputAdj,
  onOutputAdjChange,
  isSubmit,
}) => {
  const { t } = useTranslation();
  const [activeTone, setActiveTone] = useState<string | null>(outputAdj.tone);
  const [activeFormat, setActiveFormat] = useState<string | null>(
    outputAdj.format,
  );
  const [activeLength, setActiveLength] = useState<string | null>(
    outputAdj.length,
  );
  const modalRef = useRef<HTMLDivElement>(null);

  const handleOutside = useCallback(
    (e: MouseEvent | TouchEvent) => {
      onClose();
    },
    [onClose],
  );

  useOnClickOutside(modalRef, handleOutside);

  useEffect(() => {
    const adjustments = {
      tone: activeTone,
      format: activeFormat,
      length: activeLength,
    };

    onOutputAdjChange(adjustments);
  }, [activeTone, activeFormat, activeLength]);

  const handleToggle = (
    currentValue: string | null,
    setValue: React.Dispatch<React.SetStateAction<string | null>>,
    newValue: string,
  ) => {
    if (currentValue === newValue) {
      setValue(null);
    } else {
      setValue(newValue);
    }
  };

  const getButtonStyle = (isActive: boolean) =>
    isActive
      ? "border border-del-green bg-del-light-theme-green text-white px-3 py-1.5 rounded-full"
      : "border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-3 py-1.5 rounded-full hover:bg-slate-50 dark:hover:bg-slate-700";

  const modalContent = (
    <div
      ref={modalRef}
      className={`${isSubmit ? "right-6 bottom-14" : "right-6 bottom-10"} fixed modal-panel p-3 z-[999] min-w-[360px] max-h-[70vh] overflow-auto`}
    >
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-lg font-semibold text-slate-900">
          {t("chatAI.outputAdjustments")}
        </h2>
        <button
          onClick={onClose}
          className="modal-close-btn"
          aria-label="Close"
          type="button"
        >
          <FaTimes className="text-base" />
        </button>
      </div>

      <div className="mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide mb-2 text-slate-500 dark:text-slate-300">
          {t("chatAI.tone")}
        </h3>
        <div className="flex flex-wrap gap-2 text-xs">
          <button
            className={getButtonStyle(activeTone === "Professional")}
            onClick={() =>
              handleToggle(activeTone, setActiveTone, "Professional")
            }
          >
            {t("chatAI.professional")}
          </button>
          <button
            className={getButtonStyle(activeTone === "Casual")}
            onClick={() => handleToggle(activeTone, setActiveTone, "Casual")}
          >
            {t("chatAI.casual")}
          </button>
          <button
            className={getButtonStyle(activeTone === "Simple")}
            onClick={() => handleToggle(activeTone, setActiveTone, "Simple")}
          >
            {t("chatAI.simple")}
          </button>
        </div>
      </div>
      <hr className="my-3 border-slate-200 dark:border-slate-700" />

      <div className="mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide mb-2 text-slate-500 dark:text-slate-300">
          {t("chatAI.format")}
        </h3>
        <div className="flex flex-wrap gap-2 text-xs">
          <button
            className={getButtonStyle(activeFormat === "Paragraph")}
            onClick={() =>
              handleToggle(activeFormat, setActiveFormat, "Paragraph")
            }
          >
            {t("chatAI.paragraph")}
          </button>
          <button
            className={getButtonStyle(activeFormat === "E-mail")}
            onClick={() =>
              handleToggle(activeFormat, setActiveFormat, "E-mail")
            }
          >
            {t("chatAI.email")}
          </button>
          <button
            className={getButtonStyle(activeFormat === "Ideas")}
            onClick={() => handleToggle(activeFormat, setActiveFormat, "Ideas")}
          >
            {t("chatAI.ideas")}
          </button>
          <button
            className={getButtonStyle(activeFormat === "Article")}
            onClick={() =>
              handleToggle(activeFormat, setActiveFormat, "Article")
            }
          >
            {t("chatAI.article")}
          </button>
        </div>
      </div>
      <hr className="my-3 border-slate-200 dark:border-slate-700" />

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide mb-2 text-slate-500 dark:text-slate-300">
          {t("chatAI.length")}
        </h3>
        <div className="flex flex-wrap gap-2 text-xs">
          <button
            className={getButtonStyle(activeLength === "Short")}
            onClick={() => handleToggle(activeLength, setActiveLength, "Short")}
          >
            {t("chatAI.short")}
          </button>
          <button
            className={getButtonStyle(activeLength === "Medium")}
            onClick={() =>
              handleToggle(activeLength, setActiveLength, "Medium")
            }
          >
            {t("chatAI.medium")}
          </button>
          <button
            className={getButtonStyle(activeLength === "Long")}
            onClick={() => handleToggle(activeLength, setActiveLength, "Long")}
          >
            {t("chatAI.long")}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return modalContent;
  return createPortal(modalContent, document.body);
};

export { ModalOutputAdjustments };
